(ns client-portal.client.reports.components.key-metrics-stacked-bar-chart
  (:require [client-portal.client.anonymous.data :as anon.d]
            [shared.core.components.components :as c]
            [shared.core.components.tooltip :as tooltip]
            [shared.core.components.recharts.tooltip :as rc-tooltip]
            [shared.core.amelia-light.components.components :as alc]
            [client-portal.client.reports.components.ai-summary :as ai-summary]
            [client-portal.client.vi.components.tag :as tag]
            [client-portal.client.voice-insights.tag-data :as tag.d]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.sessions.drill :as vi.drill]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(defn- value-segments
  "The stacked value bar for a single part-entry (used for both the parent \"all
   day\" bar and the expanded child bars): segments sized by each value's share of
   the entry total, shaded via an opacity gradient of the tag's `color`."
  [{:keys [tag-name color values height show-labels? on-value-double-click]}]
  (let [values (->> values
                    (remove #(= "not-applicable" (:value %)))
                    (sort-by (fn [{:keys [value]}] (vi.d/tag-sort-order tag-name value))))
        total (reduce + (map :count values))
        n (count values)
        opacities (vi.d/opacity-gradient n)]
    (if (zero? total)
      [:div {:style {:flex 1
                     :font-size 11
                     :color (style/alc-color "Text/secondary")}}
       "No data"]
      (into [:div {:style {:display :flex
                           :width "100%"
                           :height height}}]
            (map-indexed
              (fn [idx {:keys [value count]}]
                (let [pct (* (/ count total) 100.0)
                      v-label (tag/tag->display-name {:value value})
                      pct-label (str (if (< pct 1.0) "<1" (util/to-precision pct :precision 0)) "%")
                      opacity (nth opacities idx)
                      first? (zero? idx)
                      last? (= idx (dec n))]
                  [tooltip/tooltip
                   {:placement :bottom
                    :arrow? false
                    :theme "transparent"
                    :tooltip-container-style {:padding 0}
                    :style (cond-> {:display :flex
                                    :align-items :center
                                    :width (str pct "%")
                                    :height "100%"
                                    :background (util/hex->rgba color opacity)
                                    :border-top-left-radius (when first? 3)
                                    :border-bottom-left-radius (when first? 3)
                                    :border-top-right-radius (when last? 3)
                                    :border-bottom-right-radius (when last? 3)}
                                   on-value-double-click
                                   (assoc :cursor :pointer))
                    :tooltip-content [rc-tooltip/tooltip-panel
                                      {:numeric-align :right
                                       :rows [{:label v-label
                                               :percent pct-label
                                               :indicator {:shape :circle
                                                           :color (util/hex->rgba color opacity)}}]
                                       :show-double-click-message? (boolean on-value-double-click)}]
                    :target-content [:div {:style {:display :flex
                                                   :align-items :center
                                                   :overflow :hidden
                                                   :width "100%"
                                                   :height "100%"
                                                   :padding "5px 0px 3px 8px"}
                                           ;; Single-click stays on the bar (expand toggle); a
                                           ;; double-click opens the filtered sessions view.
                                           :on-click (when on-value-double-click #(.stopPropagation %))
                                           :on-double-click (when on-value-double-click
                                                              #(on-value-double-click value %))}
                                     (when show-labels?
                                       [:<>
                                        [:span {:style {:flex "0 0 auto"
                                                        :font-size 10
                                                        :line-height "16px"
                                                        :font-weight style/font-medium-weight
                                                        :color (style/alc-color "Common/White")}}
                                         v-label]
                                        [:span {:style {:flex "0 0 auto"
                                                        :font-size 10
                                                        :line-height "16px"
                                                        :margin-left 4
                                                        :font-weight style/font-regular-weight
                                                        :color (util/hex->rgba "#fff" 0.8)}}
                                         pct-label]])]}]))
              values)))))

(defn- child-entry
  "Resolve a breakdown part-entry into display fields. When `location-metrics?`
   the child `:part` is a location id (labeled from `locations`); otherwise it is
   a day-part name (labeled and time-stamped from `dp-by-label`)."
  [{:keys [location-metrics? locations dp-by-label]} {:keys [part values]}]
  (if location-metrics?
    (let [loc-label (some->> locations
                             (filter #(= (:id %) (util/parse-int (str part))))
                             first
                             :label
                             (anon.d/location-label))]
      {:part part
       :label (or loc-label "Unknown")
       :sort-key (str loc-label)
       :values values})
    (let [dp (get dp-by-label part)]
      {:part part
       :label (util/capitalize-words (str part))
       :times (when (and (:start-time dp) (:end-time dp))
                (str (time/reformat-date-str (:start-time dp) "h:mm A")
                     " - "
                     (time/reformat-date-str (:end-time dp) "h:mm A")))
       :sort-key (or (:start-time dp) (str part))
       :values values})))

(defn- child-bar
  "One breakdown child row: its label (+ day-part times) and value segments."
  [{:keys [tag-name color label times values on-value-double-click]}]
  [:div {:style {:display :flex
                 :flex-direction :row
                 :align-items :center
                 :padding "0px 24px 0px 16px"
                 :gap 12}}
   [:div {:style {:width 120
                  :display :flex
                  :flex-direction :column
                  :align-items :flex-end}}
    [:span {:style {:font-size 12
                    :font-weight 400
                    :line-height 1.33
                    :color (style/alc-color "Text/secondary")
                    :text-align :right}}
     label]
    (when times
      [:span {:style {:font-size 10
                      :color (style/alc-color "Text/secondary")}}
       times])]
   [value-segments {:tag-name tag-name
                    :color color
                    :values values
                    :height 10
                    :show-labels? false
                    :on-value-double-click on-value-double-click}]])

(defn- metric-bar
  "One tag's bar: the \"all day\" aggregate, with a chevron that expands to the
   per-location / per-day-part breakdown when `expandable?` and children exist.
   The breakdown shows `view-more-count` children first, then a \"View More\"
   reveals the rest. With a `drill`, double-clicking a value segment opens VI sessions
   with that tag value, scoped to the segment's location or day part (⌘/ctrl opens a
   new tab)."
  [_]
  (let [open?* (re/ratom false)
        show-all?* (re/ratom false)]
    (fn [{:keys [expandable? tag-name label color parts location-metrics? locations dp-by-label
                 view-more-count drill drill-context report-filters]}]
      (let [mk-double-click (fn [part-kind part]
                              (when drill
                                (fn [value e]
                                  (vi.drill/open-sessions e
                                                          drill-context
                                                          drill
                                                          {:extra (cond-> [{:filter "tags" :tag tag-name :values [value]}]
                                                                          (= part-kind :location) (conj {:filter "location" :values [part]})
                                                                          (= part-kind :day-part) (conj {:filter "day-part" :values [part]}))}
                                                          report-filters))))
            child-part-kind (if location-metrics? :location :day-part)
            all-day (first (filter #(= "all day" (:part %)) parts))
            children (->> parts
                          (remove #(= "all day" (:part %)))
                          (remove #(nil? (:part %))))
            expandable? (and expandable? (not-empty children))
            entries (->> children
                         (map #(child-entry {:location-metrics? location-metrics?
                                             :locations locations
                                             :dp-by-label dp-by-label}
                                            %))
                         (sort-by :sort-key))
            view-more-count (or view-more-count 8)
            visible (if @show-all?* entries (take view-more-count entries))
            hidden (- (count entries) (count visible))]
        [:div {:style {:display :flex
                       :flex-direction :column}}
         [c/interactive
          :div {:style {:display :flex
                        :flex-direction :row
                        :align-items :center
                        :padding "8px 24px 8px 16px"
                        :gap 12}
                :hover-style (when expandable?
                               {:background-color (util/hex->rgba (style/alc-color "Background/page") 0.6)
                                :cursor :pointer})
                :on-click (when expandable?
                            #(do
                               (swap! open?* not)
                               (when-not @open?* (reset! show-all?* false))))}
          [:div {:style {:width 120
                         :display :flex
                         :align-items :center
                         :justify-content :flex-end
                         :gap 6}}
           [:span {:style {:font-size 12
                           :font-weight 400
                           :line-height 1.33
                           :color (style/alc-color "Text/primary")
                           :text-align :right}}
            label]
           (when expandable?
             [c/material-icon {:icon-type (if @open?* :expand_more :chevron_right)
                               :style {:color (style/alc-color "Text/primary")
                                       :font-size 12}}])]
          [value-segments {:tag-name tag-name
                           :color color
                           :values (:values all-day)
                           :height 24
                           :show-labels? true
                           :on-value-double-click (mk-double-click :all-day nil)}]]
         (when (and expandable? @open?*)
           (into [:div {:style {:display :flex
                                :flex-direction :column
                                :gap 6
                                :padding "6px 0px"
                                :background-color (util/hex->rgba (style/color :black :base) 0.02)}}]
                 (concat
                   (map (fn [entry]
                          [child-bar (assoc entry
                                       :tag-name tag-name
                                       :color color
                                       :on-value-double-click (mk-double-click child-part-kind (:part entry)))])
                        visible)
                   (when (pos? hidden)
                     [[:div {:style {:display :flex
                                     :justify-content :center
                                     :align-items :center
                                     :padding "0px 24px"
                                     :cursor :pointer}
                             :on-click #(reset! show-all?* true)}
                       [:span {:style {:font-size 12
                                       :line-height "18px"
                                       :font-weight style/font-regular-weight
                                       :color (style/color :blue :shade-1)}}
                        (str "View More (" (util/readable-number hidden) ")")]]]))))]))))

(defn key-metrics-stacked-bar-chart [_]
  (let [locations- (re/subscribe [:locations/by-current-concept-id-])
        day-parts- (re/subscribe [:day-parts/by-current-concept-id-])
        selected-location- (re/subscribe [:locations/current-selected-location-id-])
        drill-context- (re/subscribe [:drill/context-])]
    (fn [{:keys [data tags title subtitle expandable? view-more-count drill color-fn report-filters ai-summary]}]
      (if (util/loading? data)
        [c/loader-rectangle {:height 420}]
        (let [by-tag (into {} (map (juxt :tag-name identity)) (util/when-not-loading data))
              location-metrics? (nil? @selected-location-)
              locations (util/when-not-loading @locations-)
              dp-by-label (into {} (map (juxt :label identity)) (util/when-not-loading @day-parts-))]
          [alc/card {:title (or title "Key Metrics")
                     :icon-type :CallIntentIcon
                     :subtitle (or subtitle "Percentage of sessions with the tag")
                     :style {:padding "16px 0"
                             :width "100%"}
                     :header-style {:padding "0 16px"}}
           [ai-summary/ai-summary {:summary ai-summary
                                   :style {:margin "0 16px"}}]
           (into [:div {:style {:display :flex
                                :flex-direction :column
                                :padding-top 6}}]
                 (map (fn [{:keys [tag-name label color] :as r}]
                        (let [color-fn (or color-fn (:color-fn r))]
                          [metric-bar {:expandable? (boolean expandable?)
                                       :view-more-count view-more-count
                                       :drill drill
                                       :drill-context @drill-context-
                                       :report-filters report-filters
                                       :tag-name tag-name
                                       :label (or label (tag/tag->display-name {:name tag-name}))
                                       :color (or color
                                                  (condp = color-fn
                                                    "str->hex" (util/str->hex tag-name)
                                                    (tag.d/utterance-tag-name->color tag-name)))
                                       :parts (:parts (get by-tag tag-name))
                                       :location-metrics? location-metrics?
                                       :locations locations
                                       :dp-by-label dp-by-label}]))
                      tags))])))))
